import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, outlineButton, pillButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

function todayDateString() {
    return new Date().toISOString().slice(0, 10);
}

// Défini localement (plutôt qu'importé d'adminTheme.js) pour que ce fichier
// reste indépendant de la version d'adminTheme.js actuellement déployée —
// certains déploiements n'ont pas encore l'export `chipButton`.
const chipButton = {
    padding: '6px 12px',
    background: 'none',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: 0,
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: font,
};

const T = {
    fr: {
        tabs: { limits: 'Envois & limites', requests: 'Demandes' },
        search: 'Rechercher un utilisateur...',
        loading: 'Chargement...',
        noUsers: 'Aucun utilisateur.',
        usedToday: (used, limit) => `${used}/${limit} aujourd'hui`,
        save: 'Sauver',
        reset: 'Réinitialiser',
        invalidNumber: 'Entre un nombre valide.',
        error: (msg) => `Erreur : ${msg}`,
        limitUpdated: (username, limit) => `Limite de @${username} mise à jour : ${limit}/jour`,
        counterReset: (username) => `Compteur du jour remis à zéro pour @${username}`,
        pending: 'En attente',
        all: 'Toutes',
        noPending: 'Aucune demande en attente.',
        noRequests: 'Aucune demande.',
        perDay: (n) => `${n}/jour`,
        approved: 'Approuvé',
        denied: 'Refusé',
        approve: 'Approuver',
        deny: 'Refuser',
        approvedMsg: (username, limit) => `${username} approuvé — limite : ${limit}/jour`,
        deniedMsg: (username) => `${username} refusé`,
    },
    en: {
        tabs: { limits: 'Sends & limits', requests: 'Requests' },
        search: 'Search a user...',
        loading: 'Loading...',
        noUsers: 'No users.',
        usedToday: (used, limit) => `${used}/${limit} today`,
        save: 'Save',
        reset: 'Reset',
        invalidNumber: 'Enter a valid number.',
        error: (msg) => `Error: ${msg}`,
        limitUpdated: (username, limit) => `@${username}'s limit updated: ${limit}/day`,
        counterReset: (username) => `Today's counter reset for @${username}`,
        pending: 'Pending',
        all: 'All',
        noPending: 'No pending requests.',
        noRequests: 'No requests.',
        perDay: (n) => `${n}/day`,
        approved: 'Approved',
        denied: 'Denied',
        approve: 'Approve',
        deny: 'Deny',
        approvedMsg: (username, limit) => `${username} approved — limit: ${limit}/day`,
        deniedMsg: (username) => `${username} denied`,
    },
};

function AdminPostcardSends() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [subTab, setSubTab] = useState('limits');

    return (
        <div>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '28px', borderBottom: `1px solid ${theme.border}` }}>
                {[
                    { id: 'limits', label: t.tabs.limits },
                    { id: 'requests', label: t.tabs.requests },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setSubTab(tab.id)}
                        style={{
                            background: 'none', border: 'none',
                            borderBottom: subTab === tab.id ? `2px solid ${theme.text}` : '2px solid transparent',
                            color: subTab === tab.id ? theme.text : theme.textMuted,
                            fontSize: '12.5px', fontWeight: subTab === tab.id ? 700 : 500, fontFamily: font, cursor: 'pointer', padding: '0 0 10px',
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {subTab === 'limits' && <LimitsSection t={t} />}
            {subTab === 'requests' && <RequestsSection t={t} />}
        </div>
    );
}

function LimitsSection({ t }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editValues, setEditValues] = useState({}); // { username: newLimitString }
    const [savingUsername, setSavingUsername] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');

    const ALLOWED_ROLES = ['CREATOR', 'ORGANISATION', 'ADMIN'];

    const fetchUsers = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('users')
            .select('username, first_name, last_name, role, postcard_daily_limit, postcard_daily_sends, postcard_last_send_date')
            .in('role', ALLOWED_ROLES)
            .order('username');
        if (!error && data) setUsers(data);
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchUsers(); }, []);

    const handleSaveLimit = async (username) => {
        const raw = editValues[username];
        const newLimit = Number(raw);
        if (!raw || isNaN(newLimit) || newLimit < 0) {
            setStatusMsg(t.invalidNumber);
            return;
        }
        setSavingUsername(username);
        setStatusMsg('');

        const { error } = await supabase.from('users').update({ postcard_daily_limit: newLimit }).eq('username', username);
        if (error) {
            setStatusMsg(t.error(error.message));
        } else {
            setUsers(prev => prev.map(u => u.username === username ? { ...u, postcard_daily_limit: newLimit } : u));
            setEditValues(prev => { const next = { ...prev }; delete next[username]; return next; });
            setStatusMsg(t.limitUpdated(username, newLimit));
        }
        setSavingUsername(null);
    };

    const handleResetSendsToday = async (username) => {
        setSavingUsername(username);
        const { error } = await supabase
            .from('users')
            .update({ postcard_daily_sends: 0, postcard_last_send_date: null })
            .eq('username', username);
        if (error) {
            setStatusMsg(t.error(error.message));
        } else {
            setUsers(prev => prev.map(u => u.username === username ? { ...u, postcard_daily_sends: 0, postcard_last_send_date: null } : u));
            setStatusMsg(t.counterReset(username));
        }
        setSavingUsername(null);
    };

    const filtered = users.filter(u => u.username.toLowerCase().includes(search.toLowerCase()));
    const today = todayDateString();

    return (
        <div>
            <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.search}
                style={{ ...inputStyle, maxWidth: '320px', marginBottom: '16px' }}
            />

            {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: '0 0 16px' }}>{statusMsg}</p>}

            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : filtered.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.noUsers}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '680px' }}>
                    {filtered.map(u => {
                        const usedToday = u.postcard_last_send_date === today ? (u.postcard_daily_sends || 0) : 0;
                        const limit = u.postcard_daily_limit || 5;
                        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                        return (
                            <div key={u.username} style={{
                                display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px',
                                background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0, flexWrap: 'wrap',
                            }}>
                                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '13px', color: theme.text }}>@{u.username}</p>
                                    {fullName && <p style={{ margin: 0, fontFamily: font, fontSize: '10.5px', color: theme.textMuted }}>{fullName}</p>}
                                </div>

                                <div style={{ fontFamily: font, fontSize: '11.5px', color: theme.textMuted, flex: '0 0 auto' }}>
                                    {t.usedToday(usedToday, limit)}
                                </div>

                                <input
                                    type="number"
                                    min={0}
                                    placeholder={String(limit)}
                                    value={editValues[u.username] ?? ''}
                                    onChange={e => setEditValues(prev => ({ ...prev, [u.username]: e.target.value }))}
                                    style={{ ...inputStyle, width: '70px', flex: '0 0 auto' }}
                                />
                                <button
                                    className="wlink"
                                    onClick={() => handleSaveLimit(u.username)}
                                    disabled={savingUsername === u.username || !editValues[u.username]}
                                    style={{ ...outlineButton, padding: '6px 12px', fontSize: '10.5px', flex: '0 0 auto' }}
                                >
                                    {t.save}
                                </button>
                                <button
                                    className="wlink"
                                    onClick={() => handleResetSendsToday(u.username)}
                                    disabled={savingUsername === u.username || usedToday === 0}
                                    style={{ ...outlineButton, padding: '6px 12px', fontSize: '10.5px', flex: '0 0 auto', color: theme.textMuted }}
                                >
                                    {t.reset}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function RequestsSection({ t }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [processingId, setProcessingId] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');

    const fetchRequests = async () => {
        setLoading(true);
        let query = supabase.from('postcard_limit_requests').select('*').order('created_at', { ascending: false });
        if (filter === 'pending') query = query.eq('status', 'pending');
        const { data, error } = await query;
        if (!error && data) setRequests(data);
        setLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchRequests(); }, [filter]);

    const handleApprove = async (req) => {
        setProcessingId(req.id);
        setStatusMsg('');

        const { error: userError } = await supabase
            .from('users')
            .update({ postcard_daily_limit: req.requested_limit })
            .eq('username', req.requester_username);

        if (userError) {
            setStatusMsg(t.error(userError.message));
            setProcessingId(null);
            return;
        }

        const { error: reqError } = await supabase
            .from('postcard_limit_requests')
            .update({ status: 'approved', resolved_at: new Date().toISOString() })
            .eq('id', req.id);

        if (reqError) {
            setStatusMsg(t.error(reqError.message));
        } else {
            setStatusMsg(t.approvedMsg(req.requester_username, req.requested_limit));
            fetchRequests();
        }
        setProcessingId(null);
    };

    const handleDeny = async (req) => {
        setProcessingId(req.id);
        setStatusMsg('');

        const { error } = await supabase
            .from('postcard_limit_requests')
            .update({ status: 'denied', resolved_at: new Date().toISOString() })
            .eq('id', req.id);

        if (error) setStatusMsg(t.error(error.message));
        else {
            setStatusMsg(t.deniedMsg(req.requester_username));
            fetchRequests();
        }
        setProcessingId(null);
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                {[
                    { id: 'pending', label: t.pending },
                    { id: 'all', label: t.all },
                ].map(f => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        style={{
                            ...chipButton,
                            padding: '5px 12px', fontSize: '10.5px',
                            background: filter === f.id ? theme.text : 'none',
                            color: filter === f.id ? '#fff' : theme.text,
                        }}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: '0 0 16px' }}>{statusMsg}</p>}

            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : requests.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>
                    {filter === 'pending' ? t.noPending : t.noRequests}
                </p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '640px' }}>
                    {requests.map(req => (
                        <div key={req.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                            padding: '14px 16px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0, gap: '16px',
                        }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: '0 0 4px', fontFamily: font, fontSize: '13px', color: theme.text }}>
                                    @{req.requester_username} → {t.perDay(req.requested_limit)}
                                    {req.status !== 'pending' && (
                                        <span style={{
                                            marginLeft: '8px', fontSize: '10px', padding: '1px 6px', borderRadius: 0,
                                            color: req.status === 'approved' ? '#3a7d4f' : theme.textMuted,
                                            border: `1px solid ${theme.border}`,
                                        }}>
                                            {req.status === 'approved' ? t.approved : t.denied}
                                        </span>
                                    )}
                                </p>
                                {req.reason && (
                                    <p style={{ margin: '0 0 4px', fontFamily: font, fontSize: '12px', color: theme.textMuted }}>
                                        {req.reason}
                                    </p>
                                )}
                                <p style={{ margin: 0, fontFamily: font, fontSize: '10px', color: theme.textFaint }}>
                                    {new Date(req.created_at).toLocaleString('fr-CA')}
                                </p>
                            </div>

                            {req.status === 'pending' && (
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <button
                                        className="wlink"
                                        onClick={() => handleApprove(req)}
                                        disabled={processingId === req.id}
                                        style={{ ...pillButton, padding: '6px 14px', fontSize: '11px' }}
                                    >
                                        {t.approve}
                                    </button>
                                    <button
                                        className="wlink"
                                        onClick={() => handleDeny(req)}
                                        disabled={processingId === req.id}
                                        style={{ ...outlineButton, padding: '6px 14px', fontSize: '11px' }}
                                    >
                                        {t.deny}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminPostcardSends;